import { Repeat, Sparkles, MessageSquare, Music, Scroll } from 'lucide-react';
import { DiscoveryCard } from './discovery-card';
import { Badge } from '@/components/ui/badge';

interface RepeatedPattern {
    kind: 'Refrain' | 'Leitmotif';
    label: string;
    count: number;
    refs?: string[];
    ranges?: string[];
}

interface RepeatedPatternsCardProps {
    patterns: RepeatedPattern[];
    isExpanded: boolean;
    onToggleExpansion: () => void;
    thumbnailUrl?: string;
}

export function RepeatedPatternsCard({ patterns, isExpanded, onToggleExpansion, thumbnailUrl }: RepeatedPatternsCardProps) {
    if (!patterns || patterns.length === 0) return null;

    return (
        <DiscoveryCard
            isExpanded={isExpanded}
            onToggle={onToggleExpansion}
            icon={Sparkles}
            thumbnailUrl={thumbnailUrl}
            header={
                <div className="flex flex-col gap-1">
                    <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-primary/50">Rhythmic Elements</div>
                    <h3 className="text-xl font-serif font-medium text-foreground/90 leading-tight">Recurring Patterns</h3>
                </div>
            }
        >
            <div className="pt-4 space-y-4">
                <p className="text-xs text-muted-foreground italic mb-2">
                    These phrases or themes echo throughout the book, creating a cohesive atmospheric logic.
                </p>

                <div className="flex flex-wrap gap-3">
                    {patterns.map((pattern, idx) => (
                        <div key={idx} className="flex items-center gap-4 p-3 rounded-xl border border-border/20 bg-background/40">
                            <div className="p-1.5 rounded-lg bg-primary/10">
                                {pattern.kind === 'Refrain' ? <Music className="h-3.5 w-3.5 text-primary/60" /> : <MessageSquare className="h-3.5 w-3.5 text-primary/60" />}
                            </div>
                            <div className="flex flex-col">
                                <span className="text-xs font-bold text-primary/80 uppercase tracking-tighter">{pattern.kind}</span>
                                <span className="text-sm font-medium italic">"{pattern.label}"</span>
                            </div>
                            <Badge variant="secondary" className="ml-2 font-mono text-[10px] h-5 bg-foreground/5 text-foreground/40">{pattern.count}x</Badge>
                        </div>
                    ))}
                </div>
            </div>
        </DiscoveryCard>
    );
}
